import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ICountry } from 'country-state-city';
import { useEffect, useState } from 'react';
import SkeletonList from '../../../../../components/MUIComponents/SkeletonList';
import { ISelectedCountry } from '../../../../../interfaces/country.interface';
import CheckBox from './components/CheckBox';

interface ICandidateSportFilterProps {
  countries: ICountry[];
  label: string;
  onShowMore: () => void;
  totalCountryCount: number;
  handleCountryChange: (country: ISelectedCountry) => void;
  handleSearchChange: (data: string) => void;
  resetTrigger: boolean;
  selectedCountries: ISelectedCountry[];
  isLoading: boolean;
  isError: boolean;
}

const CandidateCountryFilter = ({
  countries,
  label: _label,
  onShowMore,
  totalCountryCount,
  handleCountryChange,
  handleSearchChange,
  resetTrigger,
  selectedCountries,
  isLoading,
  isError,
}: ICandidateSportFilterProps) => {
  const [searchText, setSearchText] = useState('');
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    setSearchText('');
    setAllSelected(false);
  }, [resetTrigger]);

  useEffect(() => {
    // Check if all countries are selected
    setAllSelected(
      countries.length > 0 &&
        countries.every((country) =>
          selectedCountries.some((sel) => sel.isoCode === country.isoCode)
        )
    );
  }, [selectedCountries, countries]);

  const handleAllCountriesChange = () => {
    if (allSelected) {
      selectedCountries.forEach((country) => handleCountryChange(country));
    } else {
      selectedCountries.forEach((country) => handleCountryChange(country));
      countries.forEach((country) =>
        handleCountryChange({ name: country.name, isoCode: country.isoCode })
      );
    }
    setAllSelected(!allSelected);
  };

  const handleSingleCountryChange = (country: ISelectedCountry) => {
    if (allSelected) {
      selectedCountries.forEach((country) => handleCountryChange(country));
      setAllSelected(false);
    }
    handleCountryChange(country);
  };

  if (isLoading) {
    return (
      <Box
        sx={{ p: '22px 16px 11px 16px' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SkeletonList />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography sx={{ p: '22px 16px 11px 16px' }} color="error" variant="h4">
        Failed to get countries
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.375rem',
        padding: '0 1rem 1.375rem',
      }}
    >
      <Typography variant="badge2" marginTop={'0.7rem'}>
        Country
      </Typography>

      <TextField
        variant="outlined"
        placeholder="Search..."
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          handleSearchChange(e.target.value.trim());
        }}
        sx={searchFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ p: 0 }}>
              <SearchIcon sx={iconSizes} />
            </InputAdornment>
          ),
        }}
      />

      <FormGroup>
        {/* Default "All" Checkbox */}
        <CheckBox
          label={'All'}
          checked={allSelected}
          onChange={handleAllCountriesChange}
        />
        {/* List of Countries */}
        {countries?.map((country) => (
          <CheckBox
            key={country.name}
            label={country.name}
            checked={
              !!selectedCountries.find(
                (selectedCountry) => selectedCountry.isoCode === country.isoCode
              )
            }
            onChange={() =>
              handleSingleCountryChange({
                name: country.name,
                isoCode: country.isoCode,
              })
            }
          />
        ))}
      </FormGroup>

      {countries && countries.length < totalCountryCount && (
        <Typography
          onClick={onShowMore}
          variant="h4"
          component="h4"
          color={'text.accent'}
          sx={{ marginTop: '10px', cursor: 'pointer' }}
        >
          + Show more
        </Typography>
      )}
    </Box>
  );
};

export default CandidateCountryFilter;

const searchFieldStyles = {
  width: '100%',
  maxWidth: { md: '16rem', lg: '20.125rem' },
  '& .MuiInputBase-root': { height: '3.125rem', padding: '0.75rem 1rem' },
  '& .MuiOutlinedInput-input': { height: '3.125rem !important', padding: 0 },
  '& .MuiSvgIcon-root': { backgroundColor: 'transparent' },
};

const iconSizes = { width: '1.25rem', height: '1.25rem' };
